import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { AlertService } from "src/service/alert.service";
import { EventService } from "src/service/event.service";

@Component({
    selector: "app-popup-request-numeric-password",
    templateUrl: "./popup-request-numeric-password.component.html",
    styleUrls: ["./popup-request-numeric-password.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PopupRequestNumericPasswordComponent
  implements OnInit, OnDestroy, iTranslation, iEvent {
  private _translateSubscription: Subscription = null;
  private _timerInactivity: any = null;

  private _lblPasswordWrong: string = "";
  public lblAskPasswordMsg: string = "";
  public titleComponent: string = "";
  public _eventSubscriptions: Array<Subscription> = null;

  @Input() correctPassword: string;
  @Input() autoCloseSecondsTimeout: number;
  @Input() titleText: string;

  constructor(
    private _translateService: TranslateService,
    private _modalController: ModalController,
    private _alertService: AlertService,
    private _eventService: EventService
  ) { }

  ngOnInit() {
    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.handleAutoCloseTimeoutInactivity();
    this.subscribeEvents();
  }

  ngOnDestroy() {
    if (this._translateSubscription) {
      this._translateSubscription.unsubscribe();
    }
    this.unsubscribeEvents();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeForceLogout.subscribe(() => {
        this._modalController.dismiss();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  //#region Translation
  public loadTranslation(): void {
    this._translateService
      .get(["dictionary.passwordWrong", "dictionary.askPasswordMessage"])
      .subscribe((translation) => {
        this._lblPasswordWrong = translation["dictionary.passwordWrong"];
        this.lblAskPasswordMsg = translation["dictionary.askPasswordMessage"];

        this.setTitleComponent();
      });
  }

  public subscribeTranslationEvent() {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }
  //#endregion

  /**
   * Callback event from numeric keypad for password submission.
   * It reset pending timeout autoclose, then restart it
   *
   * @param passwordToSubmit Pasword to submit
   */
  public onPasswordSubmission(passwordToSubmit: any) {
    this.clearTimeoutInactivity();

    this.checkInsertedPassword(passwordToSubmit);

    this.handleAutoCloseTimeoutInactivity();
  }

  /**
   * Callback event from numeric keypad for password password changed
   *
   * @param passwordChanged Password changed
   */
  public onPasswordChange(passwordChanged: any) {
    this.clearTimeoutInactivity();
    this.handleAutoCloseTimeoutInactivity();
  }

  /**
   * Set title component
   */
  private setTitleComponent(): void {
    this.titleComponent = this.titleText || this.lblAskPasswordMsg;
  }

  /**
   * Close popup passing password check result
   *
   * @param passwordCheckResult Password check result
   */
  private closePopup(passwordCheckResult: boolean) {
    this._modalController.dismiss({ PasswordResult: passwordCheckResult });
  }

  /**
   * Perform check password (with input component password)
   *
   * @param passwordToCheck Password keypad inserted
   */
  private async checkInsertedPassword(passwordToCheck: string): Promise<void> {
    if (passwordToCheck === this.correctPassword) {
      this.closePopup(true);
    } else {
      await this._alertService.presentAlertOk(
        "Password",
        "",
        this._lblPasswordWrong
      );
      this.closePopup(false);
    }
  }

  /**
   * Start timeout inactivity count.
   * When time is elapsed it fire closing component
   */
  private handleAutoCloseTimeoutInactivity(): void {
    if (this.autoCloseSecondsTimeout > 0) {
      this._timerInactivity = setTimeout(() => {
        this.closePopup(false);
      }, this.autoCloseSecondsTimeout * 1000);
    }
  }

  /**
   * Clear timeout inactivity (if needed)
   */
  private clearTimeoutInactivity(): void {
    if (this._timerInactivity) {
      clearInterval(this._timerInactivity);

      this._timerInactivity = null;
    }
  }
}
